import * as Phaser from 'phaser';
import { LocalizationManager } from '../managers/LocalizationManager';
import { UITheme } from './UITheme';

export class VolumeSlider extends Phaser.GameObjects.Container {
    private track: Phaser.GameObjects.Rectangle;
    private fill: Phaser.GameObjects.Rectangle;
    private handle: Phaser.GameObjects.Arc;
    private label: Phaser.GameObjects.Text;
    private sliderWidth: number;
    private loc: LocalizationManager;
    private value = 0;

    constructor(scene: Phaser.Scene, x: number, y: number, width: number, initialValue: number, private onChange: (value: number) => void) {
        super(scene, x, y);
        this.sliderWidth = width;
        this.loc = LocalizationManager.getInstance();
        this.value = initialValue;

        // Label
        this.label = scene.add.text(0, -30, this.getVolString(initialValue), {
            fontFamily: UITheme.body,
            fontSize: '16px',
            color: UITheme.text,
            align: 'center'
        }).setOrigin(0.5);

        // Track
        this.track = scene.add.rectangle(0, 0, width, 10, UITheme.panel).setOrigin(0.5);
        
        // Fill
        this.fill = scene.add.rectangle(-width / 2, 0, width * initialValue, 10, UITheme.accent).setOrigin(0, 0.5);

        // Handle
        const handleX = (-width / 2) + (width * initialValue);
        this.handle = scene.add.circle(handleX, 0, 12, 0xffffff)
            .setInteractive({ draggable: true, useHandCursor: true });

        // Add to container
        this.add([this.label, this.track, this.fill, this.handle]);
        if(scene.textures.exists('ui_sound')) this.add(scene.add.image(-width/2-16,-30,'ui_sound').setDisplaySize(20,20));

        const hitArea = new Phaser.Geom.Rectangle(-width / 2 - 12, -22, width + 24, 44);
        this.setInteractive(hitArea, Phaser.Geom.Rectangle.Contains)
            .on('pointerover', () => {
                this.scene.input.setDefaultCursor('pointer');
            })
            .on('pointerout', () => {
                this.scene.input.setDefaultCursor('default');
            })
            .on('pointerdown', (_pointer: Phaser.Input.Pointer, localX: number, _y: number, event?: Phaser.Types.Input.EventData) => {
                event?.stopPropagation();
                this.applyLocalX(localX);
            })
            .on('pointermove', (pointer: Phaser.Input.Pointer, localX: number) => {
                if (pointer.isDown) {
                    this.applyLocalX(localX);
                }
            });

        // Drag events
        scene.input.setDraggable(this.handle);
        this.handle.on('drag', (_: any, dragX: number) => {
            this.applyLocalX(dragX);
        });

        scene.add.existing(this);
    }

    private getVolString(value: number): string {
        return this.loc.getText('ui.sound.volume', [Math.round(value * 100)]);
    }

    public setValue(value: number) {
        let clamped = Phaser.Math.Clamp(value, 0, 1);
        this.value = clamped;
        this.handle.x = (-this.sliderWidth / 2) + (this.sliderWidth * clamped);
        this.fill.width = this.sliderWidth * clamped;
        this.label.setText(this.getVolString(clamped));
    }

    private applyLocalX(localX: number): void {
        const newX = Phaser.Math.Clamp(localX, -this.sliderWidth / 2, this.sliderWidth / 2);
        this.handle.x = newX;
        this.fill.width = newX + (this.sliderWidth / 2);

        const value = this.fill.width / this.sliderWidth;
        this.value = value;
        this.label.setText(this.getVolString(value));
        this.onChange(value);
    }
    public setFocused(focused: boolean): void { this.handle.setStrokeStyle(focused ? 3 : 0, UITheme.warning); }
    public activate(): void { this.setFocused(true); }
    public adjust(direction: number): void { this.setValue(this.value + direction * 0.05); this.onChange(this.value); }
}
